/**
 * The persisted UI language (localStorage `eh_lang`) — ONE reader for every consumer.
 *
 * The value is user-writable and not always one of our three codes: a browser extension, an
 * older build, or a link that pre-seeded it can leave "fr_CA", "fr-CA", "ES-mx" or junk.
 * Read raw, "fr_CA" matched no dictionary and the whole app fell back to English although the
 * visitor plainly asked for French. A stored value now means its PRIMARY subtag, compared
 * case-insensitively with "_" read as "-", and anything that is not a supported language is
 * English (the product default).
 *
 * No import from `@/` here: the suites that mock `@/lib/*` as virtual modules load this file
 * through a relative path, as `LanguageContext` does for `localeMerge`.
 */
export const SUPPORTED_LANGS = ["en", "fr", "es"];
const STORAGE_KEY = "eh_lang";
const FALLBACK = "en";

/** A raw stored value as a supported language code; anything unrecognised is "en". */
export function normalizeLang(raw) {
  if (typeof raw !== "string") return FALLBACK;
  const primary = raw.trim().toLowerCase().replace(/_/g, "-").split("-")[0];
  return SUPPORTED_LANGS.includes(primary) ? primary : FALLBACK;
}

/**
 * The language to use, from storage. An absent key stays absent (a first visit has stated no
 * preference); a present key that is not already canonical is rewritten to the normalised
 * value, so no later reader ever sees the raw one. Storage that throws (privacy mode,
 * disabled cookies) yields English.
 */
export function readStoredLang() {
  try {
    if (typeof window === "undefined") return FALLBACK;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return FALLBACK;
    const lang = normalizeLang(raw);
    if (raw !== lang) window.localStorage.setItem(STORAGE_KEY, lang);
    return lang;
  } catch {
    return FALLBACK;
  }
}
